import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ArrowForward from '@mui/icons-material/ArrowForward';
import LockOutlined from '@mui/icons-material/LockOutlined';
import LightbulbOutlined from '@mui/icons-material/LightbulbOutlined';
import { useLanguage } from '../../context/LanguageContext';
import { useAccessTier } from '../../hooks/useLockedTopicIds';
import { buildExamTipPath, buildExamTipsBasePath } from '../../utils/contentRoutes';
import { BORDER, INK, MUTED, MUTED_SOFT, SURFACE } from '../../pages/Practice/map/mapTokens';
import { CONTENT_MAX_WIDTH, ctaSecondarySx } from '../../styles/tokens';
import { EXAM_TIPS_TEASER } from './homeContent';
import { SectionHeading, bandSx } from './homeUi';

/**
 * Tips listed one by one rather than described as a feature.
 *
 * "Tips & strategies" is a phrase every prep site uses and it sells nothing.
 * Locked rows show the subject only (see EXAM_TIPS_TEASER) — enough to recognise
 * the moment, not enough to guess the advice and skip buying.
 *
 * The lock is honest: one tip is genuinely free and links straight through, the
 * rest are premium and say so. Teasing all six as free would convert once and
 * then produce a refund.
 *
 * For a paying visitor none of that applies — they own all six — so every row
 * turns into its real title and a link. `useAccessTier()` is null until auth and
 * the profile have both settled, and the section renders the locked form until
 * then: that is also what react-snap prerenders, so the served HTML never leaks
 * the paid titles and the upgrade never flashes for a guest.
 */
const ExamTipsTeaser = ({ tone }) => {
  const { language } = useLanguage();
  const isPremium = useAccessTier() === 'premium';

  return (
    <Box component="section" sx={bandSx(tone)}>
      {/* No chapter chip: unlike the three sections above, tips are not tied to
          one of the StartHere doors — they are useful whichever way you came in. */}
      <SectionHeading title={EXAM_TIPS_TEASER.title} subtitle={EXAM_TIPS_TEASER.subtitle} />

      <Box
        sx={{
          display: 'grid',
          gap: { xs: 1.25, md: 1.5 },
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
          maxWidth: CONTENT_MAX_WIDTH,
          mx: 'auto',
        }}
      >
        {EXAM_TIPS_TEASER.tips.map((tip) => {
          // "Free" is a selling point only while some rows are not. For a paying
          // reader every row is open, so the badge and the muting both go away.
          const isOpen = isPremium || tip.free;
          const isFree = tip.free && !isPremium;
          const row = (
            <>
              <LightbulbOutlined
                aria-hidden
                sx={{ fontSize: 19, flex: '0 0 auto', color: isFree ? '#1B7A47' : isOpen ? INK : MUTED_SOFT }}
              />
              <Typography
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  color: isOpen ? INK : MUTED,
                  fontSize: '0.9375rem',
                  fontWeight: 700,
                  lineHeight: 1.4,
                }}
              >
                {isPremium ? tip.title : tip.lockedTitle}
              </Typography>
              {isFree ? (
                <Typography
                  component="span"
                  sx={{
                    flex: '0 0 auto',
                    px: 1,
                    py: 0.25,
                    borderRadius: '6px',
                    bgcolor: '#E8F5EE',
                    color: '#1B7A47',
                    fontSize: '0.6875rem',
                    fontWeight: 900,
                  }}
                >
                  FREE
                </Typography>
              ) : isPremium ? (
                <ArrowForward aria-hidden sx={{ fontSize: 16, flex: '0 0 auto', color: MUTED_SOFT }} />
              ) : (
                <LockOutlined aria-hidden sx={{ fontSize: 16, flex: '0 0 auto', color: MUTED_SOFT }} />
              )}
            </>
          );

          const rowSx = {
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 2,
            py: 1.5,
            bgcolor: SURFACE,
            border: `1px solid ${BORDER}`,
            borderRadius: '12px',
            textDecoration: 'none',
          };

          // A locked row is not a link: it would land on a paywall, and a link
          // that goes nowhere useful is worse than a plain row. Paid readers get
          // all six as links, straight to the tip.
          return isOpen ? (
            <Box
              key={tip.id}
              component={RouterLink}
              to={buildExamTipPath(language, tip.id)}
              sx={{
                ...rowSx,
                ...(isFree && { borderColor: '#C6E6D4' }),
                transition: 'box-shadow .2s ease',
                '&:hover': { boxShadow: '0 1px 2px rgba(16,24,40,.05), 0 12px 22px -14px rgba(19,62,135,.35)' },
                '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
              }}
            >
              {row}
            </Box>
          ) : (
            <Box key={tip.id} sx={rowSx}>
              {row}
            </Box>
          );
        })}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          gap: 2,
          mt: 3,
          maxWidth: CONTENT_MAX_WIDTH,
          mx: 'auto',
        }}
      >
        <Button
          component={RouterLink}
          to={buildExamTipsBasePath(language)}
          variant="outlined"
          endIcon={<ArrowForward />}
          sx={ctaSecondarySx}
        >
          {isPremium ? EXAM_TIPS_TEASER.premiumCta : EXAM_TIPS_TEASER.cta}
        </Button>
        <Typography sx={{ color: MUTED, fontSize: '0.875rem' }}>
          {isPremium ? EXAM_TIPS_TEASER.premiumNote : EXAM_TIPS_TEASER.note}
        </Typography>
      </Box>
    </Box>
  );
};

export default ExamTipsTeaser;
